import {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from "react";

type FieldValue =
  | string
  | number
  | boolean
  | string[]
  | Record<string, unknown>;

interface BulkEditContextValue {
  enabled: boolean;
  staged: Record<string, FieldValue>;
  /**
   * Snapshot of each field's value at the moment it first staged. The
   * bulk-save diff uses this — not the raw dealInfo — so fields seeded
   * from overview fallbacks or AI placeholders aren't reported as
   * "changed" when the user never touched them.
   */
  baseline: Record<string, FieldValue>;
  stage: (fieldName: string, fieldValue: FieldValue) => void;
  reset: () => void;
  setEnabled: (next: boolean) => void;
}

const BulkEditContext = createContext<BulkEditContextValue | null>(null);

/**
 * Wraps a region (e.g. ProfileTab) so child fields can stage edits into a
 * single batch instead of saving immediately on each blur. The wrapping
 * region drives `enabled` from a top-level Edit/Save/Cancel header, then
 * commits `staged` in one go via Promise.all on Save.
 */
export function BulkEditProvider({ children }: { children: ReactNode }) {
  const [enabled, setEnabled] = useState(false);
  const [staged, setStaged] = useState<Record<string, FieldValue>>({});
  const [baseline, setBaseline] = useState<Record<string, FieldValue>>({});

  const value = useMemo<BulkEditContextValue>(
    () => ({
      enabled,
      staged,
      baseline,
      setEnabled,
      stage: (fieldName, fieldValue) => {
        setBaseline((prev) =>
          fieldName in prev ? prev : { ...prev, [fieldName]: fieldValue }
        );
        setStaged((prev) => ({ ...prev, [fieldName]: fieldValue }));
      },
      reset: () => {
        setStaged({});
        setBaseline({});
      },
    }),
    [enabled, staged, baseline]
  );

  return (
    <BulkEditContext.Provider value={value}>
      {children}
    </BulkEditContext.Provider>
  );
}

/** Returns the bulk-edit context if a provider is mounted, else null. */
export function useBulkEditMaybe(): BulkEditContextValue | null {
  return useContext(BulkEditContext);
}

/** Throws if no provider is mounted. Use only from regions that require one. */
export function useBulkEdit(): BulkEditContextValue {
  const ctx = useContext(BulkEditContext);
  if (!ctx) {
    throw new Error("useBulkEdit must be used inside <BulkEditProvider>");
  }
  return ctx;
}

/**
 * Stages the primitive's local `draft` into bulk-edit state whenever it
 * changes. No-op when no provider is mounted or bulk mode is off, so
 * primitives can call this unconditionally without affecting the regular
 * per-field save flow.
 */
export function useStageDraftInBulk(fieldName: string, draft: FieldValue) {
  const bulk = useContext(BulkEditContext);
  const enabled = bulk?.enabled ?? false;
  const stage = bulk?.stage;
  useEffect(() => {
    if (!enabled || !stage) return;
    stage(fieldName, draft);
  }, [enabled, stage, fieldName, draft]);
}
